import { motion, AnimatePresence } from 'framer-motion';
import { useLabScientist, LEVEL_NAMES, LEVEL_EMOJIS, THRESHOLDS, type ScientistLevel } from '@/hooks/use-lab-scientist';
import { X, FlaskConical } from 'lucide-react';
import { useState } from 'react';

const LEVEL_COLORS: Record<ScientistLevel, string> = {
  none: '',
  apprentice: 'from-emerald-500 to-teal-600',
  scientist: 'from-violet-500 to-purple-600',
  mad_scientist: 'from-orange-500 via-red-500 to-pink-600',
};

const LEVEL_BORDER: Record<ScientistLevel, string> = {
  none: '',
  apprentice: 'border-emerald-400/50',
  scientist: 'border-violet-400/50',
  mad_scientist: 'border-orange-400/50 animate-pulse',
};

export function ScientistBadge() {
  const { clicks, level, levelUnlocked, showBadge, dismissBadge } = useLabScientist();
  const [isExpanded, setIsExpanded] = useState(false);

  if (levelUnlocked === 'none') return null;

  const nextLevel = level === 'apprentice' ? 'scientist' : level === 'scientist' ? 'mad_scientist' : null;
  const nextThreshold = nextLevel ? THRESHOLDS[nextLevel] : null;
  const currentThreshold = level !== 'none' ? THRESHOLDS[level] : 0;
  const progress = nextThreshold ? ((clicks - currentThreshold) / (nextThreshold - currentThreshold)) * 100 : 100;

  return (
    <>
      <AnimatePresence>
        {showBadge && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: 50 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={dismissBadge}
            data-testid="scientist-celebration-overlay"
          >
            <motion.div
              initial={{ rotateY: -180, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              transition={{ type: 'spring', damping: 15, delay: 0.2 }}
              className={`relative p-8 rounded-2xl bg-gradient-to-br ${LEVEL_COLORS[level]} shadow-2xl`}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={dismissBadge}
                className="absolute top-2 right-2 p-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                data-testid="dismiss-celebration"
              >
                <X className="w-4 h-4 text-white" />
              </button>
              
              <div className="text-center text-white">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', delay: 0.4 }}
                  className="text-6xl mb-4"
                >
                  {LEVEL_EMOJIS[level]}
                </motion.div>
                <h2 className="text-2xl font-display font-bold mb-2">
                  ¡Nivel Desbloqueado!
                </h2>
                <p className="text-lg opacity-90 mb-4">
                  {LEVEL_NAMES[level]}
                </p>
                <div className="text-sm opacity-75">
                  {clicks} clics totales
                </div>
              </div>
              
              <motion.div
                className="absolute -inset-1 rounded-2xl opacity-50 blur-xl -z-10"
                style={{ background: `linear-gradient(to right, ${level === 'mad_scientist' ? '#f97316, #ef4444, #ec4899' : level === 'scientist' ? '#8b5cf6, #7c3aed' : '#10b981, #14b8a6'})` }}
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        className={`fixed bottom-4 right-4 z-50 ${isExpanded ? 'w-64' : 'w-auto'}`}
        data-testid="scientist-badge"
      >
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsExpanded(!isExpanded)}
          className={`
            flex items-center gap-2 px-3 py-2 rounded-full
            bg-gradient-to-r ${LEVEL_COLORS[levelUnlocked]}
            border-2 ${LEVEL_BORDER[levelUnlocked]}
            text-white font-medium shadow-lg
            transition-all duration-300
          `}
          data-testid="scientist-badge-button"
        >
          <FlaskConical className="w-5 h-5" />
          {isExpanded && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              className="whitespace-nowrap text-sm"
            >
              {LEVEL_NAMES[levelUnlocked]}
            </motion.span>
          )}
        </motion.button>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute bottom-full right-0 mb-2 p-4 rounded-xl bg-card border shadow-xl w-64"
              data-testid="scientist-badge-details"
            >
              <div className="text-center mb-3">
                <span className="text-3xl">{LEVEL_EMOJIS[levelUnlocked]}</span>
                <h3 className="font-display font-semibold mt-1">{LEVEL_NAMES[levelUnlocked]}</h3>
                <p className="text-sm text-muted-foreground">{clicks} clics</p>
              </div>

              {nextLevel && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Siguiente: {LEVEL_NAMES[nextLevel]}</span>
                    <span>{nextThreshold} clics</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(progress, 100)}%` }}
                      className={`h-full bg-gradient-to-r ${LEVEL_COLORS[nextLevel]}`}
                    />
                  </div>
                </div>
              )}

              {level === 'mad_scientist' && (
                <p className="text-xs text-center text-muted-foreground mt-2">
                  ¡Has alcanzado el nivel máximo! 🎉
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
}
